import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { LowLevelApi } from '@app/shared/services/state/low-level-api.service';
import { IdName, UIData } from '@app/shared/services/state/model/o30/uidata.model';
import html2canvas from 'html2canvas';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { EMPTY, forkJoin, from, Observable, of as observableOf, Subject } from 'rxjs';
import { catchError, debounceTime, groupBy, map, mergeAll, switchMap, take } from 'rxjs/operators';
import * as StackTrace from 'stacktrace-js';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorReportService implements OnDestroy {
  private deviceService = inject(DeviceDetectorService);
  private dataService = inject(DataService);
  private llapi = inject(LowLevelApi);
  private location = inject(LocationStrategy);


  private readonly DEBOUNCE_TIME_MS = 10000;

  constructor() {
    this.errorSubject
      .pipe(
        groupBy(_clientError => _clientError.message),
        map(_group => _group.pipe(
          debounceTime(this.DEBOUNCE_TIME_MS)
        )),
        mergeAll()
      )
      .subscribe(_clientError =>
        this.reportError(_clientError.message, _clientError.error));
  }

  /**
   * Fehler wird im "auth.context.ts" geworfen
   * Verbindungserror nicht posten (sobald der durchgeht, hat man sowieso wieder eine Verbindung)
   * ist meistens kein Fehler, weil wirklich keine Verbindung zum Internet vorhanden ist
   */
  private readonly exclude = [
    'adal Timeout error',
    'Network error',
    'postClientError',
    'Invalid_state'
  ];

  private errorSubject: Subject<ClientError> = new Subject();

  ngOnDestroy(): void {
    this.errorSubject.complete();
  }

  public scheduleErrorReport(error: any) {
    const message = error.message ? error.message : error.toString();

    if (message) {
      for (const str of this.exclude) {
        if (str && message.includes(str)) {
          return;
        }
      }
    }

    this.errorSubject.next({
      message,
      error
    });
  }

  private reportError(message: string, error: any) {
    const url = this.location instanceof PathLocationStrategy
      ? this.location.path() : '';

    forkJoin(
      [
        this.getStacktraceObservable(error),
        this.getAppEnvObservable(),
        this.getUiDataObservable(),
        this.getScreenshotObservable()
      ]
    )
      .pipe(
        switchMap(([stacktrace, env, uiData, screenshot]) => this.dataService.postClientError(
          message, stacktrace, url, env ? env.version : '?', env ? env.entityVersion : '?', this.getAppInfo(uiData),
          this.getDeviceDescription(this.deviceService.getDeviceInfo(),
            this.deviceService.isMobile(),
            this.deviceService.isTablet(),
            this.deviceService.isDesktop()), screenshot)),
        catchError((e) => {
            console.warn('reportError() caused an error: ', e);
            return EMPTY;
          }
        ))
      .subscribe(r => r === false && console.warn('Error report rejected'));
  }

  private getDeviceDescription(info: DeviceInfo, isMobile: boolean, isTablet: boolean, isDesktop: boolean): string {
    return `browser:    ${info.browser}\n` + 'localtime: ' + new Date().toLocaleString() + '\n' + (info.device !== 'unknown' ? `device:     ${info.device}\n` : '') +
      (!info.os_version.includes(info.os) ? `os:         ${info.os}\n` : '') + `os version: ${info.os_version}\nuserAgent:  ${info.userAgent}\n` +
      (isMobile ? 'mobile' : '') + (isTablet ? 'tablet' : '') + (isDesktop ? 'desktop' : '') +
      ` ${window && window.innerWidth ? window.innerWidth : '?'} x ${window && window.innerHeight ? window.innerHeight : '?'}`;
  }

  private getAppInfo(uiData: UIData): string {
    if (!uiData) {
      return 'uiData not available';
    }
    let result = `${this.formatIdNameLine('produkt', uiData.produkt)}` +
      `${this.formatIdNameLine('baustein', uiData.baustein)}`;

    if (uiData.vkePolizzeId) {
      if (uiData.vkePolizzeId.vkeId) {
        result += `vkeId: ${uiData.vkePolizzeId.vkeId}\n`;
      }
      if (uiData.vkePolizzeId.polizzeId) {
        result += `polizzeId: ${uiData.vkePolizzeId.polizzeId}\n`;
      }
    }
    if (uiData.vertragPositionId) {
      if (uiData.vertragPositionId.vertragId) {
        result += `vertragId: ${uiData.vertragPositionId.vertragId}\n`;
      }
      if (uiData.vertragPositionId.positionId) {
        result += `positionId: ${uiData.vertragPositionId.vertragId}\n`;
      }
    }
    result +=
      `selectedPerson: ${uiData.selectedPerson}\n` +
      `selectedKunde: ${uiData.selectedKunde}\n` +
      `selectedPolice: ${uiData.selectedPolice}\n`;
    if (uiData.konvertierungInfos?.startPolizzeId) {
      result += `startPolizzeId: ${uiData.konvertierungInfos.startPolizzeId}`;
    }
    return result;
  }

  private formatIdNameLine(label: string, idn: IdName): string {
    if (idn && (idn.id || idn.name)) {
      return `${label}: ${idn.id ? idn.id : '?'} - ${idn.name ? idn.name : '?'}\n`;
    } else {
      return '';
    }
  }

  private getScreenshotObservable(): Observable<string> {
    return from(html2canvas(window.document.body, {
      logging: false
    }))
      .pipe(
        map(canvas => canvas.toDataURL('image/png')
          .split(',')[1]),
        catchError(() => observableOf(undefined)),
        take(1));
  }

  private getStacktraceObservable(error: any): Observable<string> {
    return from(StackTrace.fromError(error))
      .pipe(
        map(_stackframes => (_stackframes as Array<any>)
          .splice(0, 20)
          .map(_sf => _sf.toString())
          .join('\n')),
        catchError(() => observableOf('-')),
        take(1)
      );
  }

  private getAppEnvObservable() {
    return this.llapi.getValue$('config.env')
      .pipe(
        catchError(() => observableOf(null)),
        take(1)
      );
  }

  private getUiDataObservable(): Observable<UIData> {
    return this.llapi.getValue$('layout.uiData')
      .pipe(
        catchError(() => observableOf(null)),
        take(1)
      );
  }
}

interface ClientError {
  message: string;
  error: any;
}
